import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SponsorsBanner from "../components/SponsorsBanner";
import { MapPin, Search, Navigation, X } from "lucide-react";

const goldIcon = new L.DivIcon({
  className: "cs-marker",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#C5A059;border:2px solid #0A0908;box-shadow:0 4px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:#0A0908;font-weight:700;font-family:serif">●</div>`,
  iconSize: [28, 28], iconAnchor: [14, 14],
});
const premiumIcon = new L.DivIcon({
  className: "cs-marker-premium",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#E5C97A,#9A7A3D);border:3px solid #0A0908;box-shadow:0 6px 18px rgba(197,160,89,0.5);display:flex;align-items:center;justify-content:center;color:#0A0908;font-weight:700;font-size:14px">★</div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const meIcon = new L.DivIcon({
  className: "cs-marker-me",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:#3B82F6;border:3px solid #fff;box-shadow:0 0 0 4px rgba(59,130,246,0.35)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

// Canada + USA (contiguous + Alaska)
const NA_CENTER = [50.0, -95.0];
const NA_BOUNDS = [[15.0, -170.0], [72.0, -50.0]];

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLon = ((b[1] - a[1]) * Math.PI) / 180;
  const s1 = Math.sin(dLat / 2) ** 2 +
    Math.cos((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) *
    Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s1));
}

function MapController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.bbox && target.bbox.length === 4) {
      // [south, north, west, east]
      map.fitBounds([[target.bbox[0], target.bbox[2]], [target.bbox[1], target.bbox[3]]], { padding: [40, 40] });
    } else if (target.center) {
      map.setView(target.center, target.zoom || 12);
    }
  }, [target, map]);
  return null;
}

export default function MapPage() {
  const { t, lang } = useI18n();
  const [cafes, setCafes] = useState([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [target, setTarget] = useState(null);
  const [myPos, setMyPos] = useState(null);
  const [geoDenied, setGeoDenied] = useState(false);
  const [askedGeo, setAskedGeo] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { api.get("/cafes").then((r) => setCafes(r.data)).catch(() => {}); }, []);

  // On first visit: ask for geolocation
  useEffect(() => {
    if (askedGeo) return;
    setAskedGeo(true);
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setMyPos(p);
        setTarget({ center: p, zoom: 12 });
      },
      () => setGeoDenied(true),
      { timeout: 8000, maximumAge: 60000 }
    );
  }, [askedGeo]);

  // Debounced search suggestions
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/geocode/search", { params: { q: query.trim() } });
        setSuggestions(data || []);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const pickSuggestion = (s) => {
    setTarget({ center: [s.latitude, s.longitude], bbox: s.bbox, zoom: 12 });
    setQuery(s.name.split(",")[0]);
    setSuggestions([]);
  };

  const centerOnMe = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const p = [pos.coords.latitude, pos.coords.longitude];
        setMyPos(p);
        setGeoDenied(false);
        setTarget({ center: p, zoom: 13 });
      },
      () => setGeoDenied(true),
      { timeout: 8000 }
    );
  };

  const located = useMemo(() => cafes.filter((c) => c.latitude != null && c.longitude != null), [cafes]);
  const withDist = useMemo(() => {
    if (!myPos) return located;
    return located
      .map((c) => ({ ...c, distance_km: haversineKm(myPos, [c.latitude, c.longitude]) }))
      .sort((a, b) => a.distance_km - b.distance_km);
  }, [located, myPos]);

  return (
    <div data-testid="map-page" className="min-h-screen text-[#F5F0EB]">
      <Header />
      <section className="pt-32 pb-6 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="overline mb-3">{lang === "en" ? "Canada · United States" : "Canada · États-Unis"}</div>
          <h1 className="font-serif text-4xl md:text-6xl font-light">{lang === "en" ? "Interactive map" : "Carte interactive"}</h1>
          <p className="mt-4 text-[#A8A39D] max-w-xl text-sm">
            {lang === "en"
              ? "Explore cafés, bakeries and pastry shops across Canada and the United States. Search a city, province, state or ZIP — or use your location."
              : "Explorez les cafés, boulangeries et pâtisseries partout au Canada et aux États-Unis. Recherchez une ville, une province, un État ou un code postal — ou utilisez votre position."}
          </p>
        </div>
      </section>

      {/* Search + Geo controls */}
      <section className="px-6 md:px-12 pb-4">
        <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="relative flex-1 w-full">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A8A39D]" />
            <input
              data-testid="map-search"
              className="luxe-input pl-11 pr-10 w-full"
              placeholder={lang === "en" ? "Search city, state, province or ZIP…" : "Rechercher une ville, un état, une province ou un code postal…"}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button data-testid="map-search-clear" onClick={() => { setQuery(""); setSuggestions([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A8A39D] hover:text-[#F5F0EB]"><X size={14} /></button>
            )}
            {suggestions.length > 0 && (
              <ul data-testid="map-search-results" className="absolute z-40 mt-2 left-0 right-0 bg-[#0E0C0B] border border-white/10 max-h-80 overflow-auto">
                {suggestions.map((s, i) => (
                  <li key={i}>
                    <button
                      data-testid={`map-search-result-${i}`}
                      onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-4 py-3 text-sm text-[#F5F0EB] hover:bg-[#141210] border-b border-white/5 last:border-b-0"
                    >
                      <div className="text-[#F5F0EB]">{s.name.split(",").slice(0, 3).join(", ")}</div>
                      <div className="text-[10px] tracking-widest uppercase text-[#A8A39D] mt-1">{s.name.split(",").slice(-2).join(",")}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {searching && query.length >= 2 && suggestions.length === 0 && (
              <div className="absolute z-40 mt-2 left-0 right-0 bg-[#0E0C0B] border border-white/10 px-4 py-3 text-xs text-[#A8A39D]">…</div>
            )}
          </div>
          <button data-testid="map-locate-me" onClick={centerOnMe} className="btn-ghost whitespace-nowrap">
            <Navigation size={14} /> {lang === "en" ? "My location" : "Ma position"}
          </button>
        </div>
        {geoDenied && (
          <div data-testid="map-geo-denied" className="max-w-[1400px] mx-auto mt-3 text-xs text-[#A8A39D] border border-white/10 px-3 py-2">
            {lang === "en"
              ? "Location access denied. You can still search or browse the map manually."
              : "Accès à la position refusé. Vous pouvez toujours rechercher ou explorer la carte manuellement."}
          </div>
        )}
      </section>

      <section className="px-6 md:px-12 pb-6">
        <div className="max-w-[1400px] mx-auto border border-white/10" style={{ height: "70vh", minHeight: 520 }}>
          <MapContainer
            center={NA_CENTER}
            zoom={4}
            minZoom={3}
            maxBounds={NA_BOUNDS}
            maxBoundsViscosity={0.6}
            worldCopyJump={false}
            style={{ height: "100%", width: "100%", background: "#0E0C0B" }}
            scrollWheelZoom={true}
            data-testid="leaflet-map"
          >
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapController target={target} />
            {myPos && (
              <>
                <Marker position={myPos} icon={meIcon}><Popup>{lang === "en" ? "You are here" : "Vous êtes ici"}</Popup></Marker>
                <Circle center={myPos} radius={800} pathOptions={{ color: "#3B82F6", weight: 1, fillOpacity: 0.05 }} />
              </>
            )}
            {located.map((c) => (
              <Marker key={c.id} position={[c.latitude, c.longitude]} icon={c.plan_id === "premium" || c.sponsored ? premiumIcon : goldIcon}>
                <Popup>
                  <div style={{ minWidth: 200 }}>
                    <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 20, color: "#0A0908", fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2, color: "#666", marginTop: 4 }}>{c.category}{c.city ? ` · ${c.city}` : ""}</div>
                    {(c.plan_id === "premium" || c.sponsored) && <div style={{ display: "inline-block", marginTop: 6, padding: "2px 6px", background: "#C5A059", color: "#0A0908", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Premium</div>}
                    {myPos && (
                      <div style={{ fontSize: 12, color: "#333", marginTop: 8 }}>
                        {(haversineKm(myPos, [c.latitude, c.longitude])).toFixed(1)} km {lang === "en" ? "away" : "de vous"}
                      </div>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <a href={`/cafe/${c.slug}`} style={{ color: "#9A7A3D", fontSize: 12, textTransform: "uppercase", letterSpacing: 2, textDecoration: "underline" }}>{lang === "en" ? "Open page →" : "Voir la fiche →"}</a>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Nearest list when we know user position */}
        {myPos && withDist.length > 0 && (
          <div data-testid="nearest-list" className="max-w-[1400px] mx-auto mt-8">
            <div className="overline mb-4">{lang === "en" ? "Nearest cafés" : "Les plus proches"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {withDist.slice(0, 4).map((c) => (
                <Link key={c.id} to={`/cafe/${c.slug}`} data-testid={`nearest-${c.slug}`} className="luxe-card group block">
                  <div className="aspect-[4/3] overflow-hidden bg-[#141210]">
                    {c.banner_url ? <img src={c.banner_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center text-[#5D4037] font-serif text-4xl">{c.name?.[0]}</div>}
                  </div>
                  <div className="p-4">
                    <div className="font-serif text-lg leading-tight">{c.name}</div>
                    <div className="text-[10px] tracking-widest uppercase text-[#A8A39D] mt-1">{c.city}</div>
                    <div className="text-xs text-[#C5A059] mt-2 tracking-wider">{c.distance_km.toFixed(1)} km</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {!located.length && (
          <div data-testid="map-empty" className="max-w-[1400px] mx-auto mt-8 stat-card text-center py-12">
            <MapPin className="mx-auto text-[#5D4037] mb-3" size={32} />
            <div className="font-serif text-2xl mb-2">{lang === "en" ? "No geolocated cafés yet" : "Aucun commerce géolocalisé pour le moment"}</div>
            <div className="text-[#A8A39D] text-sm">{lang === "en" ? "Cafés will appear on the map as soon as owners add their address." : "Les commerces apparaîtront sur la carte dès que leur adresse est renseignée."}</div>
            <Link to="/discover" className="btn-ghost mt-6 inline-flex">{lang === "en" ? "See list" : "Voir la liste"}</Link>
          </div>
        )}
      </section>
      <SponsorsBanner />
      <Footer />
    </div>
  );
}
